//Mantine
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';
import { useDocumentTitle } from '@refinedev/react-router-v6';

//Columns
import { StatementColumns } from 'columns/Statement';

//Components
import { TableAlertBanner, TableProgressBar } from 'components/common';
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { StatementDetailPanel } from './components/StatementDetailPanel';
import { StatementCards } from './components/Mobile/StatementCards';
import { StatementRowActions } from './components/StatementRowActions';

//Interface
import { Statement, Payor, Provider, Status, Tariff, Site } from 'interfaces';

//Hooks
import { useIsMobile, useIsProvider, useProviderAllSites } from 'hooks';
import { useCustomColumnSorting } from 'hooks/useCustomColumnSorting';
import { useCustomColumnVisibility } from 'hooks/useCustomColumnVisibility';

interface StatementManagementProps {
  statements: Statement[];
  payors: Payor[];
  providers: Provider[];
  statuses: Status[];
  tariffs: Tariff[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  showProviderColumn?: boolean;
  renderTopToolbarCustomActions?: MRT_TableOptions<Statement>['renderTopToolbarCustomActions'];
  sites?: Site[];
}

export const StatementManagement: React.FC<StatementManagementProps> = ({
  statements,
  payors,
  providers,
  statuses,
  tariffs,
  isError,
  isLoading,
  errorMessage,
  showProviderColumn = false,
  renderTopToolbarCustomActions,
  sites = [],
}) => {
  const title = 'Pay Statements';
  useDocumentTitle('Statements | QBilling');

  const isProvider = useIsProvider();
  const { isAllSites } = useProviderAllSites({ enabled: isProvider });
  const isMobile = useIsMobile();

  const data: Statement[] = statements;
  const columns: MRT_ColumnDef<Statement>[] = StatementColumns(
    showProviderColumn,
    sites
  );

  const initialColumnVisibility = {
    actions: true,
    startDate: true,
    endDate: true,
    totalAmount: true,
    siteID: false,
  };
  const { columnVisibility, onColumnVisibilityChange } =
    useCustomColumnVisibility({
      initialColumns: initialColumnVisibility,
      conditionalColumns: { siteID: isProvider && isAllSites },
    });
  const { sorting, onSortingChange } = useCustomColumnSorting({
    initialSorting: [{ id: 'startDate', desc: true }],
  });

  const tableOptions: MRT_TableOptions<Statement> = {
    data,
    columns,
    enableRowActions: true,
    enableExpanding: true,
    state: {
      showLoadingOverlay: isLoading,
      showSkeletons: isLoading,
      sorting,
      columnVisibility,
    },
    onSortingChange,
    onColumnVisibilityChange,
    getRowCanExpand: (row) => Boolean(row.original.statementPath),
    mantineToolbarAlertBannerProps: () => {
      return TableAlertBanner({ isError, message: errorMessage });
    },
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    renderTopToolbarCustomActions,
    renderDetailPanel: ({ row }) => {
      return row.getCanExpand() ? (
        <StatementDetailPanel
          row={row}
          payors={payors}
          providers={providers}
          statuses={statuses}
          tariffs={tariffs}
        />
      ) : null;
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        Cell: ({ row }) => {
          return <StatementRowActions row={row} />;
        },
      },
    },
  };

  return (
    <div className="statement-management">
      {!isMobile ? (
        <BillingDataGridWrapper
          title={title}
          dataTestID="statementManagement"
          tableOptions={tableOptions}
        />
      ) : (
        <StatementCards data={data} isLoading={isLoading} />
      )}
    </div>
  );
};
